import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { ReportsList } from "@circle-react/components/SettingsApp/Moderation/ReportFlaggedContentModal/ReportsList";
import { VisibilityInfo } from "@circle-react/components/SettingsApp/Moderation/ReportFlaggedContentModal/VisibilityInfo";
import { Modal } from "@circle-react-shared/uikit/ModalV2";
import { FlaggedContentActions } from "../../shared/Moderation/FlaggedContentActions";
import { useApproveRejectContent } from "../useApproveRejectContent";

const contentPortalId = "review-modal-content";
const i18nRoot = "settings.moderation.review_flagged_content_modal";

export interface ModerationInfoModalProps {
  flaggedContentId: string;
  flaggedContentType: "post" | "comment";
}

const POST_TYPE = "post";
const COMMENT_TYPE = "comment";

const ModerationInfoModalComponent = ({
  flaggedContentId,
  flaggedContentType,
}: ModerationInfoModalProps) => {
  const modal = useModal();

  const {
    approveFlaggedContent,
    rejectFlaggedContent,
    flaggedContent,
    isApproving,
    isRejecting,
  } = useApproveRejectContent(flaggedContentId, flaggedContentType);

  const { flagged_contentable, reports = [], status } = flaggedContent ?? {};
  const type = flagged_contentable?.type?.toLowerCase();
  const reportsCount = reports?.length;

  const onApprove = async () => {
    approveFlaggedContent();
    await modal.hide();
  };

  const onReject = async (reason: string | null) => {
    rejectFlaggedContent({ reason });
    await modal.hide();
  };

  if (status === undefined || (type !== POST_TYPE && type !== COMMENT_TYPE)) {
    return null;
  }

  return (
    <Modal
      isOpen={modal.visible}
      onClose={modal.hide}
      contentPortalId={contentPortalId}
    >
      <Modal.Overlay />
      <Modal.Content size="lg">
        <Modal.Header>
          <Modal.Title size="sm">
            {t("flagged_contents.moderation_info.reports_title", {
              count: reportsCount,
            })}
          </Modal.Title>
          <Modal.HeaderActions className="absolute right-0 top-0 mr-6 mt-6">
            <Modal.CloseButton onClick={modal.hide} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body className="flex flex-col !p-0">
          <VisibilityInfo content={flaggedContent} />
          <ReportsList reports={reports} />
        </Modal.Body>
        <Modal.Footer className="!p-0">
          <FlaggedContentActions
            i18nRoot={i18nRoot}
            status={status}
            isApproving={isApproving}
            isRejecting={isRejecting}
            onApprove={onApprove}
            onReject={onReject}
            contentType={type}
          />
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

export const ModerationInfoModal = ModalManager.create(
  (props: ModerationInfoModalProps) => (
    <ModerationInfoModalComponent {...props} />
  ),
);
